import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Checkbox, Dropdown, Flex, Input } from 'antd';
import { debounce, isEqual } from 'lodash';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FixedSizeList as List } from 'react-window';

import { searchASCII } from '../../util';

interface SelectFilterProps {
  options: { id: string; displayName: string }[];
  onChange?: (value: string[]) => void;
  value?: string[];
  label?: string;
  allowSearch?: boolean;
  size?: 'small' | 'middle' | 'large';
}

interface SelectFilterRef {
  focus: () => void;
}

const SelectContainer = styled(Flex)`
  & .select-filter {
    border: 1px solid #d9d9d9;
    background: #ffffff;
    padding: 0 11px;
    border-radius: 6px;
    align-items: center;
    // height: 40px;
  }
  & .select-filter-focus {
    border-color: #f97316;
    box-shadow: 0 0 0 2px rgba(255, 145, 5, 0.1);
    outline: 0;
  }
  & .select-filter-focus-label {
    color: rgba(0, 0, 0, 0.25);
  }
`;

const ItemsCard = styled(Flex)`
  max-height: 300px;
  overflow-y: auto;
`;

// const CheckboxGroupContainer = styled(Flex)`
//   flex-direction: column;
//   & .ant-checkbox-group {
//     display: flex;
//     gap: 4px;
//   }
// `;

const StyledCheckbox = styled(Checkbox)`
  display: flex;
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  border-radius: 6px;
  transition: background-color 0.3s;
  width: 100%;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
  &:last-child {
    border-bottom: none;
  }
  span:not(.ant-checkbox) {
    flex: 1 1 0%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const sizeMapper: Record<'small' | 'middle' | 'large', string> = {
  small: '22px',
  middle: '32px',
  large: '40px',
};

/**
 * A customizable multi-select filter component with:
 * - Virtualized list for performance
 * - Search functionality
 * - Select all/none options
 * - Customizable sizing
 */
export const SelectFilter = forwardRef<SelectFilterRef, SelectFilterProps>(
  (
    { label, value = [], options, allowSearch, onChange, size = 'middle' },
    ref
  ) => {
    const [searchText, setSearchText] = useState<string>('');
    const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(
      undefined
    );
    const [open, setOpen] = useState(false);
    const [checkedList, setCheckedList] = useState<string[]>(value);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownWidthRef = useRef<number | undefined>(undefined);
    const height = sizeMapper[size];

    // Memoized values
    const checkedDefaultList = useMemo(
      () => options.map((col) => col.id),
      [options]
    );

    // Get display labels for selected items
    const labelSelected = useMemo(() => {
      const selectedLabels = options
        .filter((col) => checkedList.includes(col.id))
        .map((col) => col.displayName);
      const missingIds = checkedList.filter(
        (id) => !options.some((col) => col.id === id)
      );
      return [...missingIds, ...selectedLabels];
    }, [checkedList, options]);

    // Display text for the trigger button
    const labelText = useMemo(
      () =>
        labelSelected.length === checkedDefaultList.length ||
        labelSelected.length === 0
          ? 'All'
          : labelSelected.join(', '),
      [checkedDefaultList.length, labelSelected]
    );

    // Checkbox states
    const checkAll = useMemo(
      () => checkedList.length === options.length,
      [checkedList.length, options.length]
    );
    const indeterminate = useMemo(
      () => checkedList.length > 0 && checkedList.length < options.length,
      [checkedList.length, options.length]
    );

    // Format options for checkbox group
    const checkboxOptions = useMemo(
      () => options.map((col) => ({ value: col.id, label: col.displayName })),
      [options]
    );

    // Sync with external value changes
    useEffect(() => {
      if (!open) {
        if (value.length === 0 && checkedDefaultList.length > 0) {
          setCheckedList(checkedDefaultList);
        } else if (!isEqual(value, checkedList) && Array.isArray(value)) {
          setCheckedList(value);
        }
      }
    }, [value, checkedList, open, checkedDefaultList]);

    // Measure dropdown width
    useEffect(() => {
      if (buttonRef.current && !dropdownWidth) {
        const currentWidth = buttonRef.current.offsetWidth;
        setDropdownWidth(currentWidth);
        dropdownWidthRef.current = currentWidth;
      }
    }, [dropdownWidth]);

    // Filter options based on search text
    const optionsFiltered = useMemo(
      () =>
        checkboxOptions.filter(({ label }) => searchASCII(searchText, label)),
      [checkboxOptions, searchText]
    );

    // Toggle select all/none
    const onCheckAllChange = useCallback(
      (e: any) => {
        const newCheckedList = e.target.checked ? checkedDefaultList : [];
        setCheckedList(newCheckedList);
      },
      [checkedDefaultList]
    );

    // Debounced search handler
    const searchRef = useRef(
      debounce((text: string) => setSearchText(text), 500)
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      searchRef.current(e.target.value);
    };

    // Expose focus method via ref
    useImperativeHandle(ref, () => ({
      focus: () => {
        buttonRef.current?.focus();
      },
    }));

    // Handle individual checkbox selection
    const handleSelect = useCallback(
      (value: string) => {
        const newCheckedList = checkedList.includes(value)
          ? checkedList.filter((item) => item !== value)
          : [...checkedList, value];

        setCheckedList(newCheckedList);
      },
      [checkedList]
    );

    // Handle dropdown open/close
    const handleOpenChange = (open: boolean) => {
      setOpen(open);
      if (!open) {
        const newCheckedList =
          checkedList.length === checkedDefaultList.length ? [] : checkedList;
        onChange?.(newCheckedList);
      }
    };

    /**
     * Virtualized list row renderer
     * @param index - Row index
     * @param style - Inline styles for positioning
     * @returns A row component containing a checkbox and the label of the
     * current option.
     */
    const Row = ({
      index,
      style,
    }: {
      index: number;
      style: React.CSSProperties;
    }) => {
      const options = [{ value: 'all', label: 'All' }, ...optionsFiltered];
      const item = options[index];
      const isAll = item.value === 'all';
      return (
        <Flex style={style} className="px-1 mt-1" title={item.label}>
          <StyledCheckbox
            key={item.value}
            checked={isAll ? checkAll : checkedList.includes(item.value)}
            indeterminate={isAll ? indeterminate : false}
            onChange={() => {
              if (isAll) {
                onCheckAllChange({ target: { checked: !checkAll } });
              } else {
                handleSelect(item.value);
              }
            }}
          >
            {item.label}
          </StyledCheckbox>
        </Flex>
      );
    };

    // Render dropdown content
    const renderDropdown = useCallback(
      () => (
        <Flex
          vertical
          gap={8}
          className="bg-white shadow-antd-shadow rounded-md"
          style={{ minWidth: dropdownWidth }}
        >
          {allowSearch && (
            <Flex className="p-1">
              <Input
                className="w-full"
                placeholder="Search"
                size="large"
                onChange={handleSearchChange}
                prefix={<SearchOutlined />}
              />
            </Flex>
          )}
          <ItemsCard vertical>
            <List
              height={300}
              itemCount={optionsFiltered.length + 1} // +1 for "All" option
              itemSize={40}
              width="100%"
              style={{ maxHeight: 300, height: 'unset' }}
              className="p-1 [&::-webkit-scrollbar]:w-[14px]
                  [&::-webkit-scrollbar-track]:rounded-full
                  [&::-webkit-scrollbar-track]:bg-gray-100
                  [&::-webkit-scrollbar-thumb]:rounded-full
                  [&::-webkit-scrollbar-thumb]:bg-gray-300"
            >
              {Row}
            </List>
          </ItemsCard>
        </Flex>
      ),
      [
        dropdownWidth,
        allowSearch,
        indeterminate,
        onCheckAllChange,
        checkAll,
        optionsFiltered,
        handleSelect,
      ]
    );

    return (
      <Dropdown
        open={open}
        trigger={['click']}
        onOpenChange={handleOpenChange}
        dropdownRender={renderDropdown}
        overlayStyle={{ width: 'max-content' }}
      >
        <SelectContainer
          className="w-full h-full cursor-pointer"
          ref={buttonRef}
        >
          <Flex
            rootClassName={`select-filter ${open && 'select-filter-focus'}`}
            justify="space-between"
            className="w-full"
            style={{ height }}
          >
            <span
              className={`font-semibold pr-1 ${open && 'select-filter-focus-label'}`}
            >
              {label}
            </span>
            <span title={labelText} className="truncate flex-1">
              {labelText}
            </span>
            <DownOutlined className="w-3 h-3" style={{ color: '#737373' }} />
          </Flex>
        </SelectContainer>
      </Dropdown>
    );
  }
);
